// Aba "Aditivos" do contrato. Lista aditivos (contratos filhos com
// contrato_pai_id) e permite criar novos com a opção de já criar a
// parcela financeira em conjunto.
// Wave 2 (Antd) — 2026-06-01.

import { useState } from 'react';
import axios from 'axios';
import {
  Table, Button, Card, Row, Col, Form, Input, InputNumber, DatePicker, Checkbox,
  Empty, Typography, Space, Tag, App as AntApp, Statistic, Flex,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, DollarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { API_URL } from '../../config';
import { fmt, fmtData, getToken } from './utils';
import type { Contrato } from './types';

export function AbaAditivos({ contrato, onAtualizar, onAbrir }: {
  contrato: Contrato; onAtualizar: () => void; onAbrir?: (contratoId: string) => void;
}) {
  return <AntApp><AbaAditivosInner contrato={contrato} onAtualizar={onAtualizar} onAbrir={onAbrir} /></AntApp>;
}

function AbaAditivosInner({ contrato, onAtualizar, onAbrir }: {
  contrato: Contrato; onAtualizar: () => void;
  // 2026-08-21: abre o aditivo como contrato. Um aditivo é uma linha inteira de
  // operacao.contratos — tem briefing, entregáveis, parcelas, ARTs e Drive
  // próprios — mas até aqui a tela só mostrava 6 colunas de leitura e não havia
  // caminho nenhum pra ver o que era específico dele.
  onAbrir?: (contratoId: string) => void;
}) {
  const { message } = AntApp.useApp();
  const aditivos = contrato.aditivos || [];
  const [criando, setCriando] = useState(false);
  const [valorAditivo, setValorAditivo] = useState<number | null>(null);
  const [motivo, setMotivo] = useState('');
  const [novoFim, setNovoFim] = useState<string>('');
  const [salvando, setSalvando] = useState(false);
  // Sprint F15 — opção de já criar a parcela financeira do aditivo
  const [jaCriarParcela, setJaCriarParcela] = useState(true);
  const [parcelaDesc, setParcelaDesc] = useState('Aditivo');
  const [parcelaVenc, setParcelaVenc] = useState<string>('');

  async function criarAditivo() {
    if (valorAditivo == null) { message.warning('Valor é obrigatório'); return; }
    if (jaCriarParcela && !parcelaVenc) {
      message.warning('Data de vencimento da parcela é obrigatória (ou desmarque "Criar parcela junto")');
      return;
    }
    setSalvando(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`, {
        action: 'op_criar_aditivo',
        contrato_pai_id: contrato.id,
        valor: valorAditivo,
        motivo_aditivo: motivo,
        data_termino_previsto: novoFim || undefined,
      }, { headers: { Authorization: token } });
      const aditivoCriado = r.data?.body ? JSON.parse(r.data.body) : r.data;
      if (jaCriarParcela && aditivoCriado?.id) {
        try {
          await axios.post(`${API_URL}/comercial/propostas-geradas`, {
            action: 'fin_criar_conta_receber',
            contrato_id: aditivoCriado.id,
            descricao: parcelaDesc || 'Aditivo',
            valor_previsto: valorAditivo,
            data_vencimento: parcelaVenc,
            status: 'pendente',
          }, { headers: { Authorization: token } });
        } catch (eParc: any) {
          message.warning('Aditivo criado, mas falhou ao criar a parcela: ' +
            (eParc.response?.data?.error || eParc.message));
        }
      }
      setCriando(false);
      setValorAditivo(null); setMotivo(''); setNovoFim('');
      setParcelaDesc('Aditivo'); setParcelaVenc('');
      message.success('Aditivo criado.');
      onAtualizar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  // Esta aba só aparece num aditivo quando ele tem filhos legados (9 casos em
  // prod, anteriores à trava). Ali a lista serve pra navegar, não pra criar:
  // opCriarAditivo recusa aditivo de aditivo com 400.
  const ehAditivo = !!contrato.contrato_pai_id;

  // PG numeric volta como string em node-pg — converter antes de somar, senão
  // "34392.00" + "96949.60" concatena e Number(...) vira NaN (pegadinha #1).
  const valorBase = Number(contrato.valor_contratado) || 0;
  const somaAditivos = Number(contrato.aditivos_valor_soma) || 0;
  const total = valorBase + somaAditivos;

  const columns: ColumnsType<NonNullable<Contrato['aditivos']>[number]> = [
    { title: '#', dataIndex: 'ordem', key: 'ordem', width: 50, align: 'center',
      render: (o) => <Typography.Text strong style={{ color: '#B07500' }}>{o}</Typography.Text> },
    { title: 'Número', dataIndex: 'numero', key: 'numero',
      render: (n) => <Typography.Text style={{ fontFamily: 'monospace', color: '#020D7F' }}>{n}</Typography.Text> },
    { title: 'Motivo', dataIndex: 'motivo_aditivo', key: 'motivo', ellipsis: true,
      render: (m) => m || '—' },
    { title: 'Valor', dataIndex: 'valor_contratado', key: 'valor', width: 140, align: 'right',
      render: (v) => <Typography.Text strong style={{ color: v >= 0 ? '#0a7f4a' : '#B91626' }}>
        {v >= 0 ? '+' : ''}{fmt(v)}
      </Typography.Text> },
    { title: 'Novo fim', dataIndex: 'data_termino_previsto', key: 'fim', width: 110, align: 'center',
      render: (d) => <Typography.Text type="secondary">{fmtData(d)}</Typography.Text> },
    { title: 'Criado', dataIndex: 'criado_em', key: 'criado', width: 100, align: 'center',
      render: (d) => <Typography.Text type="secondary">{fmtData(d)}</Typography.Text> },
    // A linha inteira é clicável, mas clique sem affordance ninguém descobre —
    // esta coluna é o que diz que o aditivo ABRE.
    { title: '', key: 'abrir', width: 90, align: 'right',
      render: (_, a) => (onAbrir
        ? <Button type="link" size="small" style={{ padding: 0 }}
            onClick={(e) => { e.stopPropagation(); onAbrir(a.id); }}>
            Abrir →
          </Button>
        : null) },
  ];

  return (
    <div>
      <Flex align="center" gap={16} wrap="wrap" style={{ marginBottom: 14 }}>
        <Space size="large">
          <Statistic title="Valor base" value={valorBase} prefix="R$"
            precision={0} valueStyle={{ fontSize: 16 }}
            formatter={(v) => Number(v).toLocaleString('pt-BR')} />
          <Statistic title="Aditivos" value={somaAditivos} prefix={somaAditivos >= 0 ? '+R$' : 'R$'}
            precision={0} valueStyle={{ fontSize: 16, color: somaAditivos >= 0 ? '#0a7f4a' : '#B91626' }}
            formatter={(v) => Number(v).toLocaleString('pt-BR')} />
          <Statistic title="Total contratado" value={total} prefix="R$"
            precision={0} valueStyle={{ fontSize: 16, color: '#020D7F' }}
            formatter={(v) => Number(v).toLocaleString('pt-BR')} />
        </Space>
        <div style={{ flex: 1 }} />
        {ehAditivo && (
          <Typography.Text type="secondary" style={{ fontSize: 11.5, maxWidth: 320 }}>
            Este contrato já é um aditivo — não se cria aditivo de aditivo.
            O que está listado abaixo é anterior a essa regra.
          </Typography.Text>
        )}
        {!criando && !ehAditivo && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCriando(true)}>
            Novo aditivo
          </Button>
        )}
      </Flex>

      {criando && (
        <Card size="small" title={<Typography.Text strong style={{ color: '#020D7F' }}>
          Novo aditivo — vai herdar o briefing técnico do contrato pai
        </Typography.Text>} style={{ marginBottom: 14, borderColor: '#0930D0' }}>
          <Form layout="vertical" disabled={salvando}>
            <Row gutter={12}>
              <Col xs={24} md={8}>
                <Form.Item label="Valor do aditivo (+/-) *" required>
                  <InputNumber value={valorAditivo} onChange={(v) => setValorAditivo(v as number | null)}
                    placeholder="Ex.: 12000 ou -3000" style={{ width: '100%' }} step={100} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Novo prazo final (opcional)">
                  <DatePicker value={novoFim ? dayjs(novoFim) : null}
                    onChange={(d) => setNovoFim(d ? d.format('YYYY-MM-DD') : '')}
                    format="DD/MM/YYYY" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Motivo">
                  <Input value={motivo} onChange={e => setMotivo(e.target.value)}
                    placeholder="Ex.: Acréscimo de escopo - estudo complementar" />
                </Form.Item>
              </Col>
            </Row>

            {/* Bloco opcional: parcela financeira */}
            <Card size="small" style={{ background: '#FFF8E8', borderColor: '#E0B454', marginBottom: 12 }}>
              <Checkbox checked={jaCriarParcela}
                onChange={e => setJaCriarParcela(e.target.checked)}>
                <Typography.Text strong style={{ color: '#7A5500' }}>
                  <DollarOutlined /> Já criar parcela financeira do aditivo
                </Typography.Text>
              </Checkbox>
              {jaCriarParcela && (
                <Row gutter={12} style={{ marginTop: 10 }}>
                  <Col xs={24} md={16}>
                    <Form.Item label={<Typography.Text style={{ fontSize: 10, color: '#7A5500' }}>
                      Descrição da parcela
                    </Typography.Text>} style={{ marginBottom: 8 }}>
                      <Input value={parcelaDesc} onChange={e => setParcelaDesc(e.target.value)}
                        placeholder="Aditivo" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item label={<Typography.Text style={{ fontSize: 10, color: '#7A5500' }}>
                      Data de vencimento *
                    </Typography.Text>} required style={{ marginBottom: 8 }}>
                      <DatePicker value={parcelaVenc ? dayjs(parcelaVenc) : null}
                        onChange={(d) => setParcelaVenc(d ? d.format('YYYY-MM-DD') : '')}
                        format="DD/MM/YYYY" style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>
              )}
              <Typography.Text type="secondary" style={{ fontSize: 10, display: 'block', marginTop: 6 }}>
                {jaCriarParcela
                  ? 'A parcela já vai nascer vinculada ao aditivo, status "Pendente". Você ajusta depois no Contas a Receber.'
                  : 'Você pode criar a parcela depois manualmente no Contas a Receber.'}
              </Typography.Text>
            </Card>

            <Flex justify="end" gap={8}>
              <Button onClick={() => { setCriando(false); setValorAditivo(null); setMotivo(''); }}>
                Cancelar
              </Button>
              <Button type="primary" icon={<PlusOutlined />}
                onClick={criarAditivo} disabled={valorAditivo == null} loading={salvando}>
                Adicionar aditivo
              </Button>
            </Flex>
          </Form>
        </Card>
      )}

      {aditivos.length === 0 ? (
        <Empty description='Nenhum aditivo. Use "+ Novo aditivo" pra registrar acréscimo, redução ou prazo extra.' />
      ) : (
        <Table size="small" rowKey="id" columns={columns} dataSource={aditivos} pagination={false}
          onRow={(a) => ({
            onClick: () => onAbrir?.(a.id),
            style: onAbrir ? { cursor: 'pointer' } : undefined,
          })} />
      )}
    </div>
  );
}
